import { WorkflowStepStatus } from 'skedyul-db/enums'
import { z } from 'zod/v4'
import { WorkflowRunDependencyEventSchema } from '../../workflowRunDependencies/resources/types'

/**
 * @deprecated Pending workflow action type - hooks system has been removed
 */
export type PendingWorkflowAction = {
  workflowHandle: string
  workflowId: string
  workflowVersionId: string
  triggerId?: string
  inputs: Record<string, unknown>
  threadId: string
  workplaceId: string
  hookId?: string
  config?: Record<string, unknown>
  sourceMessageId?: string
}

export const WorkflowStepInputTypeSchema = z.enum(['task', 'workflow'])

export type WorkflowStepInputType = z.infer<typeof WorkflowStepInputTypeSchema>

export type CommandResponse<T> = {
  response: T
  /** Present for audio.transcribe — speaker-labeled transcript text */
  transcript?: string
  /** Speaker-aware structured transcript from audio.transcribe */
  transcription?: {
    text: string
    language?: string
    durationMs?: number
    speakers: Array<{ id: string; label?: string }>
    utterances: Array<{
      speaker: string
      text: string
      startMs?: number
      endMs?: number
      confidence?: number
    }>
  }
  meta: {
    action:
      | 'generate'
      | 'create'
      | 'update'
      | 'delete'
      | 'find'
      | 'count'
      | 'tool'
      | 'upsertMany'
    logs?: string[]
  }
  billing: {
    tokens: number
    cost: number
  }
  /**
   * Instance ids touched/produced by the command (for tagging and realtime updates)
   */
  instanceIds?: string[]
  /**
   * Pending workflows triggered by hooks (e.g., THREAD_CREATED hooks)
   * These need to be started by the workflow execution layer
   */
  pendingWorkflows?: PendingWorkflowAction[]
}

const TimeStamp = z
  .number()
  .describe('hour of the day')
  .or(
    z.object({
      hour: z.number().describe('hour of the day'),
      minute: z.number().describe('minute of the hour'),
      second: z.number().describe('second of the minute'),
    }),
  )
  .describe('Granular time')

export const TimeWindowSchema = z.object({
  startTime: TimeStamp,
  endTime: TimeStamp,
  days: z.array(
    z.enum([
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ]),
  ),
  timezone: z.string(),
})

const WorkflowNodeWaitRelativeSchema = z.object({
  mode: z.literal('relative'),
  amount: z.number(),
  unit: z.enum([
    'second',
    'seconds',
    'minute',
    'minutes',
    'hour',
    'hours',
    'day',
    'days',
    'week',
    'weeks',
    'month',
    'months',
    'year',
    'years',
  ]),
  windows: z.array(TimeWindowSchema).optional(),
  from: z
    .string()
    .optional()
    .describe(
      'ISO datetime base for relative delay instead of now (e.g. prior step scheduledAt)',
    ),
})

const WorkflowNodeWaitAbsoluteSchema = z.object({
  mode: z.literal('absolute'),
  scheduleAt: z
    .date()
    .transform((date) => date.getTime())
    .or(z.string()),
})

export const WorkflowNodeWaitSchema = z.union([
  WorkflowNodeWaitRelativeSchema,
  WorkflowNodeWaitAbsoluteSchema,
])

export type WaitInputType = z.infer<typeof WorkflowNodeWaitSchema>

/**
 * we only support text input currently
 */
export const WorkflowStepFormSchema = z.object({
  label: z.string(),
  fields: z.array(
    z.object({
      component: z.string(),
      span: z.number(),
      mdSpan: z.number(),
      lgSpan: z.number(),
      props: z.record(
        z.string(),
        z.object({
          type: z.string(),
          label: z.string(),
          placeholder: z.string(),
          id: z.string(),
          helpText: z.string().optional(),
          iconLeft: z.string().optional(),
        }),
      ),
    }),
  ),
})

export const WorkflowStepTerminalSchema = z.object({
  label: z.string(),
  lines: z.array(
    z.object({
      value: z.string(),
      date: z.date().transform((date) => date.toISOString()),
    }),
  ),
})

export const CreateWorkflowStepSchema = z.object({
  name: z.string(),
  workflowRunId: z.string(),
  handle: z.string(),
  type: WorkflowStepInputTypeSchema,
  wait: WorkflowNodeWaitSchema.optional(),
  workflowVersionId: z.string().optional(),
  triggerId: z.string().optional(),
  sortOrder: z.number().optional().default(0),
  meta: z
    .object({
      form: WorkflowStepFormSchema.optional(),
      terminal: WorkflowStepTerminalSchema.optional(),
    })
    .optional(),
})

export const UpdateWorkflowStepSchema = CreateWorkflowStepSchema.extend({
  id: z.string(),
  status: z.nativeEnum(WorkflowStepStatus),
}).omit({
  type: true,
  sortOrder: true,
  wait: true,
})

export const RunWorkflowSchema = z.object({
  /**
   * the workflow run id
   */
  workflowRunId: z.string(),
  /**
   * The root workflow run id (the run that started the chain). If not provided,
   * the current workflowRunId is treated as the root.
   */
  rootWorkflowRunId: z.string().optional(),
  /**
   * The direct parent workflow run id (the run that spawned this run), if any.
   */
  parentWorkflowRunId: z.string().optional(),
  /**
   * App installation inherited from the parent `run:` caller, when the child
   * payload does not carry `inputs.data.appInstallationId`.
   */
  appInstallationId: z.string().optional(),
  /**
   * the parent step id (if the workflow was triggered from another workflow (subflow or wait))
   */
  parentStepId: z.string().optional(),
  /**
   * the inputs for the workflow
   */
  inputs: z.record(z.string(), z.unknown()),
  /**
   * instance ids available to the workflow (e.g., batch job item ids)
   */
  instanceIds: z.array(z.string()).optional(),
  /**
   * Initial workflow variables from the workflow file definition
   */
  variables: z.record(z.string(), z.unknown()).optional(),
  /**
   * Event context when workflow is triggered by an event subscription
   */
  eventContext: z
    .object({
      eventId: z.string(),
      eventType: z.string(),
      threadId: z.string().optional(),
      payload: z.record(z.string(), z.unknown()),
    })
    .optional(),
})

export type RunWorkflowType = z.infer<typeof RunWorkflowSchema>
export type CreateWorkflowStepType = z.infer<typeof CreateWorkflowStepSchema>
export type UpdateWorkflowStepType = z.infer<typeof UpdateWorkflowStepSchema>
export type WorkflowStepFormType = z.infer<typeof WorkflowStepFormSchema>
export type WorkflowStepTerminalType = z.infer<
  typeof WorkflowStepTerminalSchema
>

/**
 * Event context populated when workflow is triggered by an event subscription.
 * Provides normalized access to event data via {{ context.event.* }}, {{ context.thread.* }}, etc.
 */
export type WorkflowEventContext = {
  /** Event metadata */
  event?: {
    id: string
    type: string
    /** Full envelope — thread/CRM events only; app events omit payload */
    data?: Record<string, unknown>
  }
  /** Thread context - auto-populated when event.resourceType === "thread" */
  thread?: {
    id: string
    parentId?: string
    kind?: string
    status?: string
    title?: string
  }
  /** Message context - auto-populated for message events */
  message?: {
    id: string
    content?: string
    contentType?: string
    senderId?: string
  }
  /** Participant context - auto-populated for participant events */
  participant?: {
    id: string
    kind: string
    displayName?: string
    contactId?: string
    agentId?: string
    memberId?: string
    workflowId?: string
  }
}

export type WorkflowContext = {
  inputs: Record<string, unknown>
  steps: Record<string, WorkflowRunStepOutputType>
  /**
   * Dynamic workflow variables that can be set by steps and accessed via {{ variables.key }}
   */
  variables: Record<string, unknown>
  /** Workplace for the run — used by Liquid CRM app filters (`| bft: …`). */
  workplaceId?: string
  /** Workflow run id — used to resolve app installation / CRM maps for Liquid. */
  workflowRunId?: string
  /** App installation when known (trigger, webhook payload, or parent `run:`). */
  appInstallationId?: string
  /** Parent workflow run — used to inherit app installation for `run:` children. */
  parentWorkflowRunId?: string
  /**
   * Legacy thread context from ThreadWorkflowRun association
   * @deprecated Use context.thread instead for event-triggered workflows
   */
  thread?: {
    threadId?: string
    parentThreadId?: string
    sourceMessageId?: string
    /** Array of source thread IDs (from ThreadLink parent-role records) */
    parentIds?: string[]
    /** Array of source message IDs (from ThreadLink records) */
    sourceMessageIds?: string[]
    /** Full parent-role link details */
    parents?: Array<{
      parentThreadId: string
      sourceMessageId?: string | null
      role: string
      reason?: string | null
    }>
  }
  /**
   * Normalized context from event trigger.
   * Access via {{ context.event.* }}, {{ context.thread.* }}, {{ context.message.* }}, {{ context.participant.* }}
   */
  context?: WorkflowEventContext
}

export type WorkflowRunStepOutputType = {
  status: WorkflowStepStatus
  outputs: Record<string, any>
  inputs: Record<string, any>
  error?: string
}

export const WorkflowStepTagSchema = z.object({
  'contact-id': z.string().describe('The id of the contact'),
  label: z.string().describe('The label of the tag'),
  loading: z.boolean().optional().describe('Whether the tag is loading'),
  'workflow-run-id': z
    .string()
    .optional()
    .describe(
      'The id of the workflow run - can be used to reference external workflow run events, if its not provided it will be the current workflow run',
    ),
  'count-down': z
    .boolean()
    .optional()
    .describe('Whether the tag should countdown'),
  timeout: z
    .number()
    .optional()
    .describe(
      'The timeout of the tag - will be set when the step ends if not provided',
    ),
  icon: z.string().optional().describe('The icon of the tag'),
})

/**
 * Schema for loop configuration on workflow steps.
 * When a step has a loop, it executes for each item in the provided array.
 * Within the loop, templates can access:
 * - {{ loop.item }} - the current item in the iteration
 * - {{ loop.item.property }} - properties on the current item
 * - {{ loop.index }} - the zero-based index of the current iteration
 */
export const WorkflowStepLoopSchema = z.object({
  /**
   * A liquid template that resolves to the array of items to iterate over.
   * Example: "{{ steps.get-patients.outputs.response.data }}"
   */
  items: z.string().describe('Liquid template that resolves to an array'),
})

export type WorkflowStepLoopType = z.infer<typeof WorkflowStepLoopSchema>

// Define the base schema here to resolve circular dependencies
export const WorkflowNodeBaseSchema = z.object({
  /**
   * the dependencies for the workflow step, this will create listners so that external events can
   * intervene the workflow
   */
  dependencies: z
    .record(z.string(), WorkflowRunDependencyEventSchema)
    .optional(),
  /**
   * the type of step
   */
  type: WorkflowStepInputTypeSchema,
  /**
   * if we are waiting before running the next step
   */
  wait: WorkflowNodeWaitSchema.optional(),
  /**
   * the triggers that should be created for this workflow step
   * eg: ["webhooks.herpana"]
   */
  name: z.string().min(1).max(80),

  /**
   * the description of the workflow step
   */
  description: z.string().min(1).max(255),
  /**
   * the steps that should be run before this step
   */
  needs: z.array(z.string()).optional(),

  /**
   * Behavior when one or more parent dependencies are skipped.
   * - "skip" (default): Skip this step if any parent is skipped
   * - "run": Run this step even if parents are skipped
   */
  'on-parent-skip': z.enum(['skip', 'run']).optional(),

  /**
   * Behavior when one or more parent dependencies fail/error.
   * - "skip" (default): Skip this step if any parent fails
   * - "run": Run this step even if parents fail
   */
  'on-parent-error': z.enum(['skip', 'run']).optional(),

  /**
   * tags
   */
  tags: z.array(z.string()).optional(),

  /**
   * the environment of the workflow step
   */
  environment: z.array(z.string()).optional(),

  /**
   * IF the step should only run if the condition is met, if the condition is not
   * met, the step will be skipped
   */
  if: z
    .object({
      condition: z.string(),
      otherwise: z.enum(['skip', 'cancel']).default('skip'),
    })
    .or(z.string())
    .optional(),

  /**
   * Loop configuration to iterate the step over an array.
   * When provided, the step executes once for each item in the array,
   * with {{ loop.item }} and {{ loop.index }} available in templates.
   * The step output becomes an array of individual iteration outputs.
   */
  loop: WorkflowStepLoopSchema.optional(),

  /**
   * Timeout for the step execution in Temporal activity format (e.g., "30 seconds", "5 minutes", "1 hour").
   * If not provided, defaults to "30 seconds".
   */
  timeout: z.string().optional(),

  /**
   * Variables to set after step completion.
   * Values are liquid templates resolved with the step's outputs available as `outputs`.
   * These variables are added to the rolling context for subsequent steps.
   * Example: { first_name: "{{ outputs.response.data.first_name }}" }
   */
  variables: z.record(z.string(), z.string()).optional(),
})

export const WorkflowStepSubflowSchema = WorkflowNodeBaseSchema.extend({
  type: z.literal('workflow'),
  run: z
    .object({
      /**
       * the handle of the subflow
       * eg.
       *  - sending from a  public flow facebook/sendMessage@v1
       *  - sending from a private flow wfver_bmh3ahxqby8qi6h6shoi7vcu
       *  - sending from a private flow wfver_bmh3ahxqby8qi6h6shoi7vcu
       */
      handle: z.string(),
      /**
       * if the subflow should be spawned in a detached mode
       */
      spawn: z.enum(['detached', 'attached']).default('attached'),
    })
    /**
     * of if the run value is a string it just runs the subflow with the given handle
     * in attached mode
     */
    .or(z.string()),

  /**
   * the inputs for the subflow
   */
  inputs: z.record(z.string(), z.string()),
})

export type WorkflowStepSubflowType = z.infer<typeof WorkflowStepSubflowSchema>
